import transparentBackground from "@/assets/images/transparent.jpg";
import type { SlideFields } from "@/types/Slide";
import type { WordVocabularySlideData } from "@ai-teach/types";
import { AiFillPicture } from "react-icons/ai";
import { FaMusic, FaVideo } from "react-icons/fa";

interface WordVocabularyThumbnailProps {
  slide: SlideFields;
}

export function WordVocabularyThumbnail({ slide }: WordVocabularyThumbnailProps) {
  const vocabularySlideData = slide.slideData as WordVocabularySlideData;
  const isVideoCard = vocabularySlideData.mediaType === "video";
  const hasVideo = isVideoCard && !!vocabularySlideData.video?.src;
  const hasImage = !isVideoCard && !!vocabularySlideData.image?.src;
  const hasAudio = !!vocabularySlideData.audio?.src;

  return (
    <div className="bg-accent-50 shadow-sm p-2 h-full flex flex-col justify-center overflow-hidden">
      {/* Card media section */}
      {hasVideo ? (
        <div className="relative aspect-square border-2 border-accent-400 w-full flex items-center justify-center bg-black rounded-lg overflow-hidden shrink-0">
          <video
            src={vocabularySlideData.video?.src ?? undefined}
            className="w-full h-full object-cover"
            muted
            preload="metadata"
          />
          <div className="absolute bottom-1.5 right-1.5 z-40 w-7 h-7 rounded-full bg-accent-500 text-white flex items-center justify-center shadow-md">
            <FaVideo className="text-xs" />
          </div>
        </div>
      ) : hasImage ? (
        <div className="relative aspect-square border-2 border-accent-400 w-full flex items-center justify-center bg-gray-200 dark:bg-gray-800 rounded-lg overflow-hidden shrink-0">
          <img
            src={transparentBackground}
            alt="transparent-bg"
            className="w-full h-full object-cover absolute z-0"
          />
          <img
            src={vocabularySlideData.image.src ?? undefined}
            alt={vocabularySlideData.correctAnswer || "image"}
            className="w-full h-full object-cover z-30"
          />
          {hasAudio && (
            <div className="absolute bottom-1.5 right-1.5 z-40 w-7 h-7 rounded-full bg-accent-500 text-white flex items-center justify-center shadow-md">
              <FaMusic className="text-xs" />
            </div>
          )}
        </div>
      ) : hasAudio ? (
        <div className="relative aspect-square border-2 border-accent-400 w-full flex items-center justify-center bg-gradient-to-br from-accent-100 to-accent-200 dark:from-gray-800 dark:to-gray-900 rounded-lg overflow-hidden shrink-0">
          <div className="flex flex-col items-center justify-center gap-3 p-4 text-center">
            <div className="w-20 h-20 rounded-full bg-accent-500 text-white flex items-center justify-center shadow-lg">
              <FaMusic className="text-4xl" />
            </div>

            <div className="space-y-1 max-w-full">
              <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                Audio File
              </div>

              <div className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[180px]">
                {vocabularySlideData.audio.filename}
              </div>
            </div>

            <div className="flex gap-1">
              <span className="w-2 h-6 rounded-full bg-accent-400 animate-pulse" />
              <span className="w-2 h-10 rounded-full bg-accent-500 animate-pulse" />
              <span className="w-2 h-8 rounded-full bg-accent-400 animate-pulse" />
              <span className="w-2 h-12 rounded-full bg-accent-500 animate-pulse" />
              <span className="w-2 h-6 rounded-full bg-accent-400 animate-pulse" />
            </div>
          </div>
        </div>
      ) : (
        <div className="relative aspect-square border-2 border-accent-400 w-full flex items-center justify-center bg-gray-200 dark:bg-gray-800 rounded-lg overflow-hidden shrink-0">
          <AiFillPicture className="text-gray-400 dark:text-gray-600 text-4xl" />
        </div>
      )}

      {/* Answer */}
      <div className="relative overflow-hidden flex items-center justify-center py-4 mt-3 bg-white rounded-lg border-accent-400 shadow-md">
        <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
          {vocabularySlideData.correctAnswer || "No Correct Answer"}
        </h2>
      </div>
    </div>
  );
}
